
// Main navigation languages - expanded to 40 languages
export type Language = 
  // Original 6 languages
  'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 
  // Nordic languages
  'da' | 'no' | 'fi' | 'sv' | 'is' |
  // Central European languages
  'cs' | 'sk' | 'sl' | 'pl' |
  // Baltic languages
  'et' | 'lv' | 'lt' |
  // Other European languages
  'el' | 'nl' | 'ro' | 'bg' | 'hr' | 'hu' | 'uk' | 'he' |
  // Asian developed countries
  'ko' | 'ja' | 'zh' |
  // Other major languages
  'ar' | 'hi' | 'ru' | 'tr' | 'id' |
  // Additional Asian languages
  'th' | 'vi' | 'ms' | 'ur' | 'bn' | 'fa' | 'tl' | 'sw';

export type CityLanguage = Language;

export type LanguageText = Partial<{
  [key in Language]: string;
}>;

export type TranslationCategory = {
  [key: string]: LanguageText;
};

export type Translations = {
  [category: string]: TranslationCategory;
};

export const languageNames: Record<Language, string> = {
  // Original 6
  'en': 'English',
  'es': 'Español',
  'fr': 'Français',
  'de': 'Deutsch',
  'it': 'Italiano',
  'pt': 'Português',
  // Nordic
  'da': 'Dansk',
  'no': 'Norsk',
  'fi': 'Suomi',
  'sv': 'Svenska',
  'is': 'Íslenska',
  // Central European
  'cs': 'Čeština',
  'sk': 'Slovenčina',
  'sl': 'Slovenščina',
  'pl': 'Polski',
  // Baltic
  'et': 'Eesti',
  'lv': 'Latviešu',
  'lt': 'Lietuvių',
  // Other European
  'el': 'Ελληνικά',
  'nl': 'Nederlands',
  // Asian
  'ko': '한국어',
  'ja': '日本語',
  'zh': '中文',
  // Other major
  'ar': 'العربية',
  'hi': 'हिन्दी',
  'ru': 'Русский',
  'tr': 'Türkçe',
  'id': 'Bahasa Indonesia',
  // Additional Asian
  'th': 'ไทย',
  'vi': 'Tiếng Việt',
  'ms': 'Bahasa Melayu',
  'ur': 'اردو',
  'bn': 'বাংলা',
  'fa': 'فارسی',
  'tl': 'Tagalog',
  // Additional European
  'ro': 'Română',
  'bg': 'Български',
  'hr': 'Hrvatski',
  'hu': 'Magyar',
  'uk': 'Українська',
  'he': 'עברית',
  'sw': 'Kiswahili'
};

export const languageFlags: Record<Language, string> = {
  // Original 6
  'en': '🇬🇧',
  'es': '🇪🇸',
  'fr': '🇫🇷',
  'de': '🇩🇪',
  'it': '🇮🇹',
  'pt': '🇵🇹',
  // Nordic
  'da': '🇩🇰',
  'no': '🇳🇴',
  'fi': '🇫🇮',
  'sv': '🇸🇪',
  'is': '🇮🇸',
  // Central European
  'cs': '🇨🇿',
  'sk': '🇸🇰',
  'sl': '🇸🇮',
  'pl': '🇵🇱',
  // Baltic
  'et': '🇪🇪',
  'lv': '🇱🇻',
  'lt': '🇱🇹',
  // Other European
  'el': '🇬🇷',
  'nl': '🇳🇱',
  // Asian
  'ko': '🇰🇷',
  'ja': '🇯🇵',
  'zh': '🇨🇳',
  // Other major
  'ar': '🇸🇦',
  'hi': '🇮🇳',
  'ru': '🇷🇺',
  'tr': '🇹🇷',
  'id': '🇮🇩',
  // Additional Asian
  'th': '🇹🇭',
  'vi': '🇻🇳',
  'ms': '🇲🇾',
  'ur': '🇵🇰',
  'bn': '🇧🇩',
  'fa': '🇮🇷',
  'tl': '🇵🇭',
  // Additional European
  'ro': '🇷🇴',
  'bg': '🇧🇬',
  'hr': '🇭🇷',
  'hu': '🇭🇺',
  'uk': '🇺🇦',
  'he': '🇮🇱',
  'sw': '🇰🇪'
};

// CityLanguage uses the same as Language now
export const cityLanguageNames = languageNames;
export const cityLanguageFlags = languageFlags;

// Main languages for primary navigation (most important)
export const primaryLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];

// All supported languages grouped by region
export const languagesByRegion = {
  western: ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl'] as Language[],
  nordic: ['da', 'no', 'fi', 'sv', 'is'] as Language[],
  central: ['cs', 'sk', 'sl', 'pl'] as Language[],
  baltic: ['et', 'lv', 'lt'] as Language[],
  southern: ['el'] as Language[],
  asian: ['ko', 'ja', 'zh', 'hi', 'id', 'th', 'vi', 'ms', 'bn'] as Language[],
  middle_east: ['ar', 'tr', 'ur', 'fa'] as Language[],
  eastern: ['ru'] as Language[]
};
